import { batch } from 'react-redux';
import Api from '../../api';
import ACTIONS_TYPES from '../../constants/types';
import allActions from './index';
import allMarshallers from '../../marshallers';
import ENDPOINTS, { ENDPOINTS_SEGMENTS } from '../../constants/endpoints';
import { countLastValidStep } from '../../utils/FormDataMappers';
import {
  getFormSession,
  createFormSessionFromOrganisationTokenSession,
  createFormSessionFromGenericRegistration,
} from '../../services/accreditation-forms-service';
import { FORM_SESSION_TOKEN_PARAM } from '../../constants/formParams';
import {
  FORM_SESSION_STATUS,
  FORM_SESSION_VALIDATION_STATUS,
} from '../../utils/formSession';
import {
  BACKEND_RESPONSE_STATUS,
  APP_ERRORS,
} from '../../constants/backendResponse';
import i18next from '../../translations/i18n';
import {
  createMyFormMapping,
  validateFormSessionTokenApi,
} from '../../services/app-user/app-user';
import projectsService from '../../services/projects-service';
import { isAuthenticated } from '../../utils/authService';

const getPreviewMockedData = (tenantId) => {
  return Api.get(
    ENDPOINTS.GET_PREVIEW_MOCKED_DATA.replace(
      ENDPOINTS_SEGMENTS.TENANT_ID,
      tenantId,
    ),
  );
};

const setFormInitialValues = (initialValues) => {
  return {
    type: ACTIONS_TYPES.SET_FORM_INITIAL_VALUES,
    payload: initialValues,
  };
};

const setLoader = (loaderStatus) => {
  return { type: ACTIONS_TYPES.SET_LOADER, payload: loaderStatus };
};

const setMessageFromBackend = (status) => {
  return { type: ACTIONS_TYPES.SET_MESSAGE_FROM_BACKEND, payload: status };
};

export const setAppError = (error) => {
  return { type: ACTIONS_TYPES.SET_APP_ERROR, payload: error };
};

const setAppConfigLoadStatus = (value) => {
  return { type: ACTIONS_TYPES.SET_APP_CONFIG_LOAD_STATUS, payload: value };
};

const getFormDefinition = (tenantId, formDefId) => {
  const response = Api.get(
    `${ENDPOINTS.GET_FORM_DEFINITION.replace(
      ENDPOINTS_SEGMENTS.TENANT_ID,
      tenantId,
    ).replace(ENDPOINTS_SEGMENTS.FORM_DEF_ID, formDefId)}`,
  );
  return response;
};

const setAppConfig = (tenantId, formSessionData, isCreateFormSessionError) => {
  return async (dispatch) => {
    let formSession = formSessionData;
    let validationResponse;
    if (isCreateFormSessionError) {
      validationResponse = formSessionData?.validationResponse;

      formSession = formSession?.details;
    }

    const formDefinition = await getFormDefinition(
      tenantId,
      formSession.formDefId,
    );
    const formSessionParams = allMarshallers.formSessionMarshaller.marshal({
      tenantId,
      ...formSession,
      fields: formSession.preview
        ? (await getPreviewMockedData(tenantId)).fields
        : formSession.fields,
    });

    const globalConfigParams =
      allMarshallers.globalConfigMarshaller.marshal(formDefinition);
    const termsConfigParams =
      allMarshallers.termsConfigMarshaller.marshal(formDefinition);
    const formConfigParams = allMarshallers.formConfigMarshaller.marshal(
      formDefinition,
      validationResponse,
    );
    const customFields =
      allMarshallers.customFieldsMarshaller.marshal(formDefinition);
    batch(() => {
      dispatch(
        allActions.formSessionActions.setFormSessionAction(formSessionParams),
      );
      dispatch(
        allActions.globalConfigActions.setGlobalConfigAction(
          globalConfigParams,
        ),
      );
      dispatch(
        allActions.termsConfigActions.setTermsConfigAction(termsConfigParams),
      );
      dispatch(
        allActions.formConfigActions.setFormConfigAction(formConfigParams),
      );
      dispatch(
        allActions.formElementsActions.setFormElementsAction(
          formConfigParams.steps,
        ),
      );
      dispatch(allActions.formConfigActions.setCustomFields(customFields));

      if (formSession.preview) {
        dispatch(allActions.appConfigActions.setPreviewForm());
      }

      if (globalConfigParams?.defaultLanguage) {
        i18next.changeLanguage(globalConfigParams?.defaultLanguage);
      }

      dispatch(setAppConfigLoadStatus(true));
    });

    // REDIRECT TO LAST VALID STEP
    if (!formSession.preview && formSessionParams.fields) {
      const stepToRedirect = countLastValidStep(formSessionParams.fields);
      for (let i = 0; i < stepToRedirect; i++) {
        dispatch(allActions.formConfigActions.setAcceptedSteps(i, true));
      }

      dispatch(allActions.appConfigActions.setAccessToRegister(true));
      dispatch(allActions.formConfigActions.setFormStep(stepToRedirect));
    }
  };
};

const getFormSessionInfo = async (tenantId, formSessionToken) => {
  const formSession = await getFormSession(tenantId, formSessionToken);

  if (isAuthenticated() && formSession && formSession?.token) {
    try {
      await validateFormSessionToken(tenantId, {
        ...(formSession.eventId && { eventId: formSession.eventId }),
        formDefId: formSession.formDefId,
        projectId: formSession.projectId,
        formSession: formSession,
      });
    } catch (error) {}
    // dispatch(setAppError(APP_ERRORS.USER_ACCOUNT_NOT_APPROVED));
    // dispatch(setLoader(false));
    // isCreateFormSessionError = true;
  }

  return formSession;
};

const validateFormSessionToken = async (
  tenantId,
  { eventId, formDefId, projectId, formSession },
) => {
  let validateFormSessionTokenResponse;

  try {
    const response = await validateFormSessionTokenApi(tenantId, {
      ...(eventId && { eventId }),
      formDefId,
      projectId,
      sessionToken: formSession.token,
    });
    validateFormSessionTokenResponse = response;
  } catch (error) {
    if (
      error.request?.response?.statusReason ===
      BACKEND_RESPONSE_STATUS.USER_ACCOUNT_NOT_APPROVED
    ) {
      dispatch(setAppError(APP_ERRORS.USER_ACCOUNT_NOT_APPROVED));
      dispatch(setLoader(false));
    }
    if (
      error.request?.response?.statusReason ===
      BACKEND_RESPONSE_STATUS.USER_ACCOUNT_REJECTED
    ) {
      dispatch(setAppError(APP_ERRORS.USER_ACCOUNT_REJECTED));
      dispatch(setLoader(false));
    }
  }

  if (validateFormSessionTokenResponse) {
    const response = await createMyFormMapping({
      projectId,
      formDefId,
      ...(eventId && { eventId }),
      ...(formSession?.token && { sessionToken: formSession.token }),
    });
    if (response && validateFormSessionTokenResponse?.formPath) {
      // window.location.search = validateFormSessionTokenResponse.formPath;

      const search = new URLSearchParams(
        validateFormSessionTokenResponse.formPath,
      ).toString();
      dispatch(setURLparams(`?${search}`));

      history.replace(`?${search}`);

      // const newUrl = `${window.location.pathname}?${search}`;
      // window.history.replaceState({}, '', newUrl);

      return;
    }
  }

  return validateFormSessionTokenResponse;
};

const getAppConfigAction = (appConfig, history) => {
  return async (dispatch) => {
    const {
      tenantId,
      formId,
      formSessionToken,
      organisationFormSessionToken,
      preview,
      projectId,
      formDefId,
      eventId,
    } = appConfig;
    let formSession;

    let isCreateFormSessionError = false;
    // PREVIEW FORM - WITH PREFILLED DATA
    if (preview) {
      formSession = { formDefId: formId, preview: true };
    }

    // ACCREDITATION FORM
    if (formSessionToken) {
      formSession = await getFormSessionInfo(
        tenantId,
        formSessionToken,
        history,
      );
    }

    // FORM ALREADY SUBMITTED
    if (formSession?.status === FORM_SESSION_STATUS.SUBMITTED) {
      dispatch(setAppError(APP_ERRORS.FORM_SUBMITTED));
    }

    // if (formSession?.status === FORM_SESSION_STATUS.PENDING) {
    //   dispatch(setAppError(APP_ERRORS.FORM_PENDING));
    // }

    // ACCREDITATION ORGANISATION FORM
    if (organisationFormSessionToken) {
      try {
        formSession = await createFormSessionFromOrganisationTokenSession(
          tenantId,
          organisationFormSessionToken,
        );

        if (formSession?.token) {
          const queryParams = {
            [FORM_SESSION_TOKEN_PARAM]: `${tenantId}-${formSession.token}`,
          };

          const formSessionInfo = await getFormSessionInfo(
            tenantId,
            formSession.token,
          );
          if (formSessionInfo?.status === FORM_SESSION_STATUS.SUBMITTED) {
            dispatch(setAppError(APP_ERRORS.FORM_SUBMITTED));
          }

          dispatch(
            setAppConfig(tenantId, formSessionInfo, isCreateFormSessionError),
          );

          const search = new URLSearchParams(queryParams).toString();
          // const newUrl = `${window.location.pathname}?${search}`;
          dispatch(setURLparams(`?${search}`));
          history.replace(`?${search}`);
        }

        // window.location.search = new URLSearchParams(queryParams);
        // const queryParams = { [FORM_SESSION_TOKEN_PARAM]: `${tenantId}-${formSession.token}` };
        // const currentPath = window.location.pathname;
        // const newUrl = `${currentPath}?${new URLSearchParams(queryParams)}`;
        // history.replace(newUrl);
        return;
      } catch (error) {
        if (
          error.request?.response?.statusReason !==
          BACKEND_RESPONSE_STATUS.QUOTA_EXCEEDED
        ) {
          return;
        }

        // ASSIGNMENT QUOTA EXCEEDED
        formSession = {
          formDefId: error.request?.response?.formDefinitionId,
        };
        dispatch(setAppError(APP_ERRORS.QUOTA_EXCEEDED));
      }
    }

    // Registration Generic Form (Copy link)
    if (projectId) {
      try {
        formSession = await createFormSessionFromGenericRegistration(
          tenantId,
          projectId,
          formDefId,
          eventId,
        );

        /* [HIDEMS-3303] In case BE validation failed API throws 400 with form details
        but not responding with formSession?.token, hence we need to make it conditional.
        As per [HIDEMS-3303] requirement we need to show error popup with form in background,
        for this FE required form details in 'createSession' api even it throws 400.
        */

        if (formSession?.token) {
          const queryParams = {
            [FORM_SESSION_TOKEN_PARAM]: `${tenantId}-${formSession.token}`,
          };

          // if (isAuthenticated()) {
          //   const validateFormSessionTokenResponse =
          //     await validateFormSessionToken(tenantId, {
          //       ...(eventId && { eventId }),
          //       formDefId,
          //       projectId,
          //       formSession,
          //     });

          //   if (validateFormSessionTokenResponse?.formPath) {
          //     // dispatch(setUserAlreadyAvailableFormPath(validateFormSessionTokenResponse.formPath));
          //     // dispatch(allActions.appConfigActions.setAppError(APP_ERRORS.FORM_PENDING));
          //     // dispatch(setLoader(false));
          //     // dispatch(setAppConfig(tenantId, formSession, isCreateFormSessionError));
          //     // return;
          //   }
          // }

          const formSessionInfo = await getFormSessionInfo(
            tenantId,
            formSession.token,
          );
          if (formSessionInfo?.status === FORM_SESSION_STATUS.SUBMITTED) {
            dispatch(setAppError(APP_ERRORS.FORM_SUBMITTED));
          }

          dispatch(
            setAppConfig(tenantId, formSessionInfo, isCreateFormSessionError),
          );

          const search = new URLSearchParams(queryParams).toString();
          // const newUrl = `${window.location.pathname}?${search}`;
          dispatch(setURLparams(`?${search}`));
          // console.log('newUrl', newUrl);
          history.replace(`?${search}`);

          // window.history.replaceState({}, '', newUrl);

          //  window.location.search = new URLSearchParams(queryParams);
          return;
        } else {
          isCreateFormSessionError = true;
        }
      } catch (error) {
        const defaultLanguage = error.request?.response?.defaultLanguage;

        if (defaultLanguage) {
          dispatch(
            allActions.globalConfigActions.setGlobalLanguages([
              defaultLanguage,
            ]),
          );
          i18next.changeLanguage(defaultLanguage);
          // dispatch(setLanguage(error.request?.response?.defaultLanguage))
        }

        if (
          error.request?.response?.statusReason !==
          BACKEND_RESPONSE_STATUS.QUOTA_EXCEEDED
        ) {
          return;
        }

        // ASSIGNMENT QUOTA EXCEEDED
        formSession = {
          formDefId: error.request?.response?.formDefinitionId,
        };
        dispatch(setAppError(APP_ERRORS.QUOTA_EXCEEDED));
      }
    }

    dispatch(setAppConfig(tenantId, formSession, isCreateFormSessionError));
  };
};

const setAccessToRegister = (access) => {
  return { type: ACTIONS_TYPES.UNBLOCK_REGISTER_FORM, payload: { access } };
};

const setApplicationSent = (isSent) => {
  return { type: ACTIONS_TYPES.SET_APPLICATION_SENT, payload: { isSent } };
};

const setProjectImageRules = (rules) => {
  return { type: ACTIONS_TYPES.SET_PROJECT_IMAGE_RULES, payload: rules };
};

const fetchProjectImageRules = (projectId, tenantId) => {
  return async (dispatch) => {
    try {
      const resp = await projectsService.getProjectImageRules(projectId, tenantId);
      if (resp && resp.data) {
        dispatch(setProjectImageRules(resp.data));
      }
    } catch (error) {
      // non-fatal: project image rules are optional
    }
  };
};

const setValidationErrors = (status, errors) => {
  return {
    type: ACTIONS_TYPES.SET_VALIDATION_ERRORS,
    payload: { status, errors },
  };
};

const setResultCode = (resultCode) => {
  return {
    type: ACTIONS_TYPES.SET_RESULT_CODE,
    payload: resultCode,
  };
};

const setLanguage = (lng) => {
  return {
    type: ACTIONS_TYPES.SET_LANGUAGE,
    payload: lng,
  };
};

const setServerError = (errorMessage) => {
  return {
    type: ACTIONS_TYPES.SET_SERVER_ERROR,
    payload: errorMessage,
  };
};
const setURLparams = (urlParams) => {
  return {
    type: ACTIONS_TYPES.SET_URL_PARAMS,
    payload: urlParams,
  };
};

const setAccountRegisterUrl = (url) => {
  return {
    type: ACTIONS_TYPES.SET_ACCOUNT_REGISTER_URL,
    payload: url,
  };
};

const setErrorPage = (status) => {
  return {
    type: ACTIONS_TYPES.SET_ERROR_PAGE,
    payload: status,
  };
};

const setPreviewForm = () => {
  return {
    type: ACTIONS_TYPES.SET_PREVIEW_FORM,
  };
};

export const setUserAlreadyAvailableFormPath = (formPath) => {
  return {
    type: ACTIONS_TYPES.SET_USER_ALREADY_AVAILABLE_FORM_PATH,
    payload: formPath,
  };
};

export default {
  getAppConfigAction,
  setAppConfigLoadStatus,
  setAccessToRegister,
  setApplicationSent,
  setFormInitialValues,
  setLoader,
  setMessageFromBackend,
  setValidationErrors,
  setResultCode,
  setServerError,
  setURLparams,
  setErrorPage,
  setPreviewForm,
  setAppError,
  setUserAlreadyAvailableFormPath,
  setLanguage,
  setAccountRegisterUrl,
  fetchProjectImageRules,
};
